import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  faCalendar,
  faCirclePlus,
  faFile,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faCheckCircle,
  faSpinner,
  faXmark,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Alert, AlertTitle } from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import { Progress } from '@repo/videa-components/src/ui/progress';
import { useQueryClient } from '@tanstack/react-query';

import {
  uploadFileToS3,
  useCreateAttachment,
  useDeleteAttachments,
  useReferralAttachments,
  useUpdateAttachment,
} from '@/react-query/useReferralAttachments';

interface AttachmentUploadTabProps {
  referralId: string;
  onUploadStateChange?: (isUploading: boolean) => void;
}

export const AttachmentUploadTab = ({
  referralId,
  onUploadStateChange,
}: AttachmentUploadTabProps) => {
  const queryClient = useQueryClient();
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<
    Array<{ id: string; fileName: string; progress: number }>
  >([]);
  const [alert, setAlert] = useState<{
    variant: 'default' | 'destructive';
    title: string;
  } | null>(null);

  // API hooks
  const { data: attachmentsData, refetch: refetchAttachments } =
    useReferralAttachments(referralId);
  const createAttachmentMutation = useCreateAttachment();
  const deleteAttachmentsMutation = useDeleteAttachments();
  const updateAttachmentMutation = useUpdateAttachment();

  const attachments = useMemo(
    () => attachmentsData?.attachments || [],
    [attachmentsData?.attachments],
  );

  // Auto-dismiss alert after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files) return;

      setIsProcessingFiles(true);
      onUploadStateChange?.(true);

      // Immediately add files to uploading state
      const newUploadingFiles = Array.from(files).map((file, index) => ({
        id: `uploading-${Date.now()}-${index}`,
        fileName: file.name,
        progress: 0,
      }));
      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      try {
        const uploadPromises = Array.from(files).map(async (file, index) => {
          const uploadingFileId = newUploadingFiles[index].id;

          // Simulate progress updates
          const progressInterval = setInterval(() => {
            setUploadingFiles((prev) =>
              prev.map((f) =>
                f.id === uploadingFileId
                  ? {
                      ...f,
                      progress: Math.min(f.progress + Math.random() * 20, 90),
                    }
                  : f,
              ),
            );
          }, 200);

          try {
            const { uploadUrl } = await createAttachmentMutation.mutateAsync({
              referralId,
              fileName: file.name,
            });

            // Upload file to S3
            await uploadFileToS3(uploadUrl, file);

            // Complete progress
            setUploadingFiles((prev) =>
              prev.map((f) =>
                f.id === uploadingFileId ? { ...f, progress: 100 } : f,
              ),
            );

            clearInterval(progressInterval);
          } catch (error) {
            clearInterval(progressInterval);
            throw error;
          }
        });

        await Promise.all(uploadPromises);

        // Remove uploading files from state
        setUploadingFiles((prev) =>
          prev.filter((f) => !newUploadingFiles.some((nf) => nf.id === f.id)),
        );

        // Invalidate and refetch the attachments query
        await queryClient.invalidateQueries({
          queryKey: ['referralAttachments', referralId],
        });
        await refetchAttachments();

        setAlert({
          variant: 'default',
          title: 'Files uploaded successfully',
        });
      } catch (error) {
        // Remove failed uploads from state
        setUploadingFiles((prev) =>
          prev.filter((f) => !newUploadingFiles.some((nf) => nf.id === f.id)),
        );

        setAlert({
          variant: 'destructive',
          title: 'Failed to upload files. Please try again.',
        });
      } finally {
        setIsProcessingFiles(false);
        onUploadStateChange?.(false);
      }
    },
    [
      referralId,
      createAttachmentMutation,
      refetchAttachments,
      queryClient,
      onUploadStateChange,
    ],
  );

  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      await handleFileSelect(event.target.files);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      await handleFileSelect(event.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const removeAttachment = useCallback(
    async (attachmentId: string) => {
      try {
        await deleteAttachmentsMutation.mutateAsync({
          referralId,
          attachmentIds: [attachmentId],
        });

        // Invalidate and refetch the attachments query
        await queryClient.invalidateQueries({
          queryKey: ['referralAttachments', referralId],
        });
        await refetchAttachments();

        setAlert({
          variant: 'default',
          title: 'Attachment removed successfully',
        });
      } catch (error) {
        setAlert({
          variant: 'destructive',
          title: 'Failed to remove attachment. Please try again.',
        });
      }
    },
    [referralId, deleteAttachmentsMutation, refetchAttachments, queryClient],
  );

  const updateAttachmentDate = useCallback(
    async (attachmentId: string, attachmentDate: string) => {
      try {
        await updateAttachmentMutation.mutateAsync({
          referralId,
          attachmentId,
          data: { attachmentDate },
        });

        // Invalidate and refetch attachments
        queryClient.invalidateQueries(['referralAttachments', referralId]);
        await refetchAttachments();

        setAlert({
          variant: 'default',
          title: 'Attachment date updated successfully',
        });
      } catch (error) {
        setAlert({
          variant: 'destructive',
          title: 'Failed to update attachment date. Please try again.',
        });
      }
    },
    [referralId, updateAttachmentMutation, refetchAttachments, queryClient],
  );

  // Don't render if referralId is not available yet
  if (!referralId) {
    return (
      <div className="vai-flex vai-items-center vai-justify-center vai-py-8">
        <FontAwesomeIcon
          icon={faSpinner}
          className="vai-mr-2 vai-h-4 vai-w-4 vai-animate-spin"
        />
        <Trans>Loading attachment options...</Trans>
      </div>
    );
  }

  return (
    <div className="vai-space-y-6">
      <div className="vai-text-sm vai-text-gray-600">
        <Trans>
          Upload any additional files you&apos;d like to attach to this
          referral.
        </Trans>
      </div>

      {/* Upload Area */}
      <div
        className="vai-relative vai-bg-zinc-50 vai-border-2 vai-border-dashed vai-border-zinc-300 hover:vai-border-zinc-400 vai-rounded-lg vai-py-8 vai-px-6 vai-text-center vai-transition-colors"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="vai-flex vai-flex-col vai-items-center vai-space-y-4">
          <FontAwesomeIcon
            icon={faCirclePlus}
            className="vai-h-[26px] vai-w-[26px] vai-text-zinc-400"
          />
          <div className="vai-text-sm vai-text-zinc-900">
            <Trans>
              Drag files here or{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const fileInput = document.getElementById(
                    'file-input',
                  ) as HTMLInputElement;
                  fileInput?.click();
                }}
                disabled={
                  isProcessingFiles || createAttachmentMutation.isLoading
                }
                className="vai-text-blue-600 hover:vai-text-blue-800 hover:vai-underline vai-cursor-pointer vai-bg-transparent vai-border-none vai-p-0 vai-font-inherit"
              >
                browse
              </button>
            </Trans>
          </div>

          <input
            id="file-input"
            type="file"
            multiple
            onChange={handleFileInputChange}
            disabled={isProcessingFiles || createAttachmentMutation.isLoading}
            className="vai-hidden"
          />
        </div>
      </div>

      {/* Attachments List */}
      {(attachments.length > 0 || uploadingFiles.length > 0) && (
        <div className="vai-space-y-2">
          <h3 className="vai-text-sm vai-font-medium vai-text-gray-700">
            <Trans>Attached Files</Trans>
          </h3>
          <div className="vai-space-y-2 vai-max-h-60 vai-overflow-y-auto">
            {/* Uploaded attachments */}
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="vai-border vai-border-blue-100 vai-bg-[#EFF6FF] vai-rounded-lg vai-p-3 vai-text-foreground"
              >
                <div className="vai-flex vai-items-center vai-space-x-3 vai-mb-2">
                  <FontAwesomeIcon
                    icon={faFile}
                    className="vai-h-[15px] vai-w-[19px] vai-text-zinc-400 vai-flex-shrink-0"
                  />
                  <div className="vai-flex vai-items-center vai-space-x-2 vai-flex-1">
                    <p className="vai-text-sm vai-font-bold vai-truncate">
                      {attachment.fileName}
                    </p>
                    {attachment.status === 'UPLOADED' && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="vai-h-4 vai-w-4 vai-text-primary vai-flex-shrink-0"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.id)}
                    disabled={deleteAttachmentsMutation.isLoading}
                    className="vai-text-muted-foreground hover:vai-text-foreground vai-bg-transparent vai-border-none vai-p-0 vai-cursor-pointer vai-flex-shrink-0"
                  >
                    <FontAwesomeIcon
                      icon={faXmark}
                      className="vai-h-4 vai-w-4"
                    />
                  </button>
                </div>

                {/* Loading bar for pending attachments */}
                {attachment.status === 'PENDING' && (
                  <div className="vai-w-full vai-bg-gray-200 vai-rounded-full vai-h-1">
                    <div className="vai-bg-primary vai-h-1 vai-rounded-full vai-animate-pulse vai-w-full"></div>
                  </div>
                )}

                {/* Date picker for uploaded attachments */}
                {attachment.status === 'UPLOADED' && (
                  <div className="vai-mt-3 vai-space-y-2">
                    <label
                      htmlFor={`attachment-date-${attachment.id}`}
                      className="vai-text-sm vai-text-gray-600 vai-font-medium"
                    >
                      <Trans>Date</Trans>
                    </label>
                    <Input
                      id={`attachment-date-${attachment.id}`}
                      type="date"
                      value={attachment.attachmentDate || ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        if (newDate) {
                          updateAttachmentDate(attachment.id, newDate);
                        }
                      }}
                      disabled={updateAttachmentMutation.isLoading}
                      className="vai-h-8 vai-text-xs !vai-w-36"
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Uploading files */}
            {uploadingFiles.map((uploadingFile) => (
              <div
                key={uploadingFile.id}
                className="vai-border vai-border-blue-100 vai-bg-[#EFF6FF] vai-rounded-lg vai-p-3 vai-text-foreground"
              >
                <div className="vai-flex vai-items-center vai-space-x-3 vai-mb-2">
                  <FontAwesomeIcon
                    icon={faFile}
                    className="vai-h-[15px] vai-w-[19px] vai-text-zinc-400 vai-flex-shrink-0"
                  />
                  <div className="vai-flex vai-items-center vai-space-x-2 vai-flex-1">
                    <p className="vai-text-sm vai-font-bold vai-truncate">
                      {uploadingFile.fileName}
                    </p>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="vai-h-4 vai-w-4 vai-animate-spin vai-text-primary vai-flex-shrink-0"
                    />
                  </div>
                </div>

                {/* Progress bar */}
                <div className="vai-w-full vai-bg-gray-200 vai-rounded-full vai-h-1">
                  <Progress
                    value={uploadingFile.progress}
                    className="vai-h-1"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alert */}
      {alert && (
        <div className="vai-fixed vai-top-4 vai-right-4 vai-z-50 vai-max-w-sm">
          <Alert variant={alert.variant}>
            <AlertTitle>{alert.title}</AlertTitle>
          </Alert>
        </div>
      )}
    </div>
  );
};
